/**
 * Item Variant Draft Context
 * 
 * Provides variant draft state to widgets during item creation.
 * Similar to ItemCreateMediaContext, but for variant configuration.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { VariantDraft } from '@/lib/erp/variants';
import { createDefaultVariantDraft, VARIANT_CAPABILITY_SCHEMA } from '@/lib/erp/variants';
import { generatePlannedVariantRows } from '../variants/utils';

interface ItemVariantDraftContextValue {
  variantDraft: VariantDraft;
  setVariantDraft: (draft: VariantDraft | ((prev: VariantDraft) => VariantDraft)) => void;
  selectedVariantIds: Set<string>;
  setSelectedVariantIds: (ids: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  /** SKU overrides by variant key (variantKey -> custom item_code) */
  skuOverridesByKey: Record<string, string>;
  /** Set SKU override for a variant key */
  setSkuOverride: (variantKey: string, itemCode: string) => void;
  /** Clear SKU override for a variant key (revert to auto-generated) */
  clearSkuOverride: (variantKey: string) => void;
}

const ItemVariantDraftContext = createContext<ItemVariantDraftContextValue | null>(null);

/**
 * Hook to access variant draft context.
 * Throws if used outside provider.
 */
export function useItemVariantDraft(): ItemVariantDraftContextValue {
  const context = useContext(ItemVariantDraftContext);
  if (!context) {
    throw new Error('useItemVariantDraft must be used within ItemVariantDraftProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if context is missing.
 * Useful for widgets that might render outside provider.
 */
export function useOptionalItemVariantDraft(): ItemVariantDraftContextValue | null {
  return useContext(ItemVariantDraftContext);
}

interface ItemVariantDraftProviderProps {
  children: React.ReactNode;
  initialDraft?: VariantDraft;
}

/**
 * Provider component for variant draft state.
 * Should wrap ItemDetailPageContent in create mode.
 */
export function ItemVariantDraftProvider({
  children,
  initialDraft,
}: ItemVariantDraftProviderProps) {
  const [variantDraft, setVariantDraftState] = useState<VariantDraft>(
    initialDraft ?? createDefaultVariantDraft()
  );

  // Initialize selection state with all variants selected (up to maxTotalVariants)
  const [selectedVariantIds, setSelectedVariantIdsState] = useState<Set<string>>(() => {
    if (variantDraft.mode === 'item_attribute' && variantDraft.attributes.length > 0) {
      const capability = VARIANT_CAPABILITY_SCHEMA[variantDraft.mode];
      const allRows = generatePlannedVariantRows(variantDraft, { limit: capability.maxTotalVariants });
      return new Set(allRows.map(row => row.id));
    }
    return new Set();
  });

  // Update selection when draft changes (reset to all selected)
  useEffect(() => {
    if (variantDraft.mode === 'item_attribute' && variantDraft.attributes.length > 0) {
      const capability = VARIANT_CAPABILITY_SCHEMA[variantDraft.mode];
      const allRows = generatePlannedVariantRows(variantDraft, { limit: capability.maxTotalVariants });
      setSelectedVariantIdsState(new Set(allRows.map(row => row.id)));
    } else {
      setSelectedVariantIdsState(new Set());
    }
  }, [variantDraft]);

  const setVariantDraft = useCallback(
    (draft: VariantDraft | ((prev: VariantDraft) => VariantDraft)) => {
      setVariantDraftState((prev) => {
        if (typeof draft === 'function') {
          return draft(prev);
        }
        return draft;
      });
    },
    []
  );

  const setSelectedVariantIds = useCallback(
    (ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setSelectedVariantIdsState((prev) => {
        if (typeof ids === 'function') {
          return ids(prev);
        }
        return ids;
      });
    },
    []
  );

  // SKU overrides state (persists across draft changes as long as variantKey exists)
  const [skuOverridesByKey, setSkuOverridesByKey] = useState<Record<string, string>>({});

  const setSkuOverride = useCallback((variantKey: string, itemCode: string) => {
    setSkuOverridesByKey((prev) => ({
      ...prev,
      [variantKey]: itemCode.trim(),
    }));
  }, []);

  const clearSkuOverride = useCallback((variantKey: string) => {
    setSkuOverridesByKey((prev) => {
      const next = { ...prev };
      delete next[variantKey];
      return next;
    });
  }, []);

  return (
    <ItemVariantDraftContext.Provider value={{ 
      variantDraft, 
      setVariantDraft,
      selectedVariantIds,
      setSelectedVariantIds,
      skuOverridesByKey,
      setSkuOverride,
      clearSkuOverride,
    }}>
      {children}
    </ItemVariantDraftContext.Provider>
  );
}
